import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle, ArrowRight, Eye, EyeOff } from 'lucide-react';

const LOGO_URL = '/logo-autohub360.png';
const SIDE_IMG = 'https://images.unsplash.com/photo-1638850846828-fcb8f537180a?auto=format&fit=crop&w=800&q=80';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname
    ? location.state.from.pathname + (location.state.from.search || '')
    : '/home';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result.success) {
        // Platform owner goes directly to admin panel
        if (email === 'ntoraubally@entrepotautomontreal.ca') {
          navigate('/super-admin', { replace: true });
        } else {
          navigate(from, { replace: true });
        }
      } else {
        setError(result.error);
        setLoading(false);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('An error occurred. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="login-page">
      {/* Nav */}
      <nav className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => navigate('/')} data-testid="login-logo">
            <img src={LOGO_URL} alt="AutoHub360" className="h-14 w-auto" />
          </div>
          <div className="flex items-center gap-3">
            {/* Signup button temporarily disabled */}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Left: Image */}
        <div className="hidden lg:block lg:w-1/2 relative">
          <img
            src={SIDE_IMG}
            alt="Car in showroom"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent" />
          <div className="absolute bottom-12 left-10 right-10">
            <p className="text-[#FF8A3B] text-xs font-bold uppercase tracking-widest mb-3">
              Dealer Management Platform
            </p>
            <h2 className="text-3xl font-black text-white leading-tight tracking-tight">
              Vos operations<br />en un clin d'oeil.
            </h2>
            <p className="text-white/50 text-sm mt-3 max-w-sm">
              Track sales, manage inventory, and close deals faster — all from one place.
            </p>
          </div>
        </div>

        {/* Right: Login Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-16">
          <div className="w-full max-w-sm">
            {/* Mobile logo */}
            <div className="lg:hidden flex justify-center mb-8">
              <img src={LOGO_URL} alt="AutoHub360" className="h-16 w-auto" />
            </div>

            <h1 className="text-2xl font-black text-gray-900 tracking-tight mb-1">
              Welcome back
            </h1>
            <p className="text-sm text-gray-400 mb-8">
              Sign in to your dealership dashboard
            </p>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl flex items-center gap-2 text-sm" data-testid="login-error">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div>
                <Label className="text-[11px] text-gray-500 font-medium">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@dealership.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  className="mt-1 bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-300 rounded-xl h-11 text-sm focus-visible:ring-[#2BAEF5]"
                  data-testid="login-email"
                />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-[11px] text-gray-500 font-medium">Mot de passe</Label>
                  <button type="button" onClick={() => navigate('/forgot-password')} className="text-[11px] text-[#2BAEF5] hover:underline font-medium" data-testid="forgot-password-link">
                    Mot de passe oublié?
                  </button>
                </div>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    className="bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-300 rounded-xl h-11 text-sm pr-10 focus-visible:ring-[#2BAEF5]"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 rounded-2xl font-bold text-sm transition-all flex items-center justify-center gap-2
                  bg-[#2BAEF5] text-white hover:bg-[#1a9de4] shadow-lg shadow-[#2BAEF5]/20
                  disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Signing in...
                  </span>
                ) : (
                  <>Sign In <ArrowRight className="w-4 h-4" /></>
                )}
              </button>
            </form>

            {/* Free trial signup - temporarily disabled
            <div className="mt-8 text-center">
              <p className="text-sm text-gray-400">
                Don't have an account?{' '}
                <button
                  onClick={() => navigate('/signup')}
                  className="text-[#FF8A3B] font-semibold hover:underline"
                  data-testid="login-to-signup"
                >
                  Start your free trial
                </button>
              </p>
            </div>
            */}

            <div className="mt-10 pt-6 border-t border-gray-100 text-center">
              <p className="text-[10px] text-gray-400">
                &copy; {new Date().getFullYear()} autohub360.ca &mdash; All rights reserved
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
